import { getAppVersion } from '../utils/version';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '../Components/ErrorBoundary';
import SetupWarningBanner from '../Components/SetupWarningBanner';
import { LogOut, Sparkles } from 'lucide-react';
import AssistantPanel from '../Components/Assistant/AssistantPanel';
import { useFeatures } from '../hooks/useFeatures';
import { MainNavItem, useNavigationData } from '../hooks/navigation/useNavigationData';
import { settingsMenuItems, profileMenuItems, isFeatureVisible, isMenuItemActive } from './navigationConfig';
import MobileMenu from './MobileMenu';
import "./Navigation.css"

// Shrinks the font size until the text fits on one line within the parent's width
const ShrinkToFitLabel = ({ text, className }: { text: string; className: string }) => {
    const spanRef = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const el = spanRef.current;
        if (!el) return;
        let cancelled = false;
        const fit = () => {
            if (cancelled) return;
            el.style.fontSize = '';
            let size = parseFloat(getComputedStyle(el).fontSize);
            while (el.scrollWidth > el.clientWidth && size > 5) {
                size -= 0.5;
                el.style.fontSize = `${size}px`;
            }
        };
        fit();
        // Font metrics change once the web font loads, so fit again when ready
        document.fonts?.ready.then(fit);
        return () => { cancelled = true; };
    }, [text]);

    return <span ref={spanRef} className={className}>{text}</span>;
};

// Sidebar flyouts render on document.body: the sidebar is a fixed scroll container,
// and Safari clips fixed descendants to it, hiding a flyout placed beside it.
const SidebarPortal = ({ children }: { children: React.ReactNode }) => createPortal(children, document.body);

const isOutside = (event: MouseEvent, ...refs: React.RefObject<HTMLElement | null>[]) =>
    refs.every((ref) => !ref.current?.contains(event.target as Node));

// A main section with sub-pages (Hyresadmin, Parkering): the button opens a flyout beside
// it, like Inställningar, placed from the button's position when opened.
const MainNavFlyout = ({ item }: { item: MainNavItem }) => {
    const location = useLocation();
    const features = useFeatures();
    const [top, setTop] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (top === null) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (isOutside(event, containerRef, menuRef)) setTop(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [top]);

    useEffect(() => { setTop(null); }, [location.pathname]);

    const toggle = () => {
        if (top !== null) {
            setTop(null);
            return;
        }
        const rect = containerRef.current?.getBoundingClientRect();
        setTop(rect ? rect.top : 0);
    };

    return (
        <div className="main-nav-flyout-container" ref={containerRef}>
            <button
                type="button"
                className={`menu-icon-button ${item.isActive ? 'active' : ''}`}
                onClick={toggle}
                title={item.label}
                aria-expanded={top !== null}
                aria-haspopup="menu"
            >
                <img src={item.icon} alt="" className="menu-icon-image" />
                <span className="menu-item-label">{item.label}</span>
            </button>
            {top !== null && (
                <SidebarPortal>
                    <div className="settings-dropdown main-nav-flyout" style={{ top }} role="menu" ref={menuRef}>
                        <div className="settings-dropdown-header">
                            <span className="settings-dropdown-title">{item.label}</span>
                        </div>
                        <div className="settings-dropdown-divider" />
                        {(item.children ?? [])
                            .filter((child) => isFeatureVisible(child.feature, features))
                            .map((child) => (
                                <Link
                                    key={child.path}
                                    to={child.path}
                                    role="menuitem"
                                    className={`settings-dropdown-item ${isMenuItemActive(child, location.pathname) ? 'active' : ''}`}
                                    onClick={() => setTop(null)}
                                >
                                    <child.icon size={18} />
                                    <span>{child.label}</span>
                                </Link>
                            ))}
                    </div>
                </SidebarPortal>
            )}
        </div>
    );
};

const Navigation = () => {
    const location = useLocation();

    const {
        userName,
        organizationName,
        customerLogo,
        isAdmin,
        isSuperadmin,
        mainNavItems,
        activeTodosCount,
        logOut,
    } = useNavigationData();

    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
    const [showAssistant, setShowAssistant] = useState(false);
    const features = useFeatures();
    // Superadmin-only, and only while not switched off under Inställningar → Funktioner
    const showAssistantEntry = isSuperadmin && features?.aiAssistant === true;
    const profileDropdownRef = useRef<HTMLDivElement>(null);
    const settingsDropdownRef = useRef<HTMLDivElement>(null);
    const profileMenuRef = useRef<HTMLDivElement>(null);
    const settingsMenuRef = useRef<HTMLDivElement>(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOutside(event, profileDropdownRef, profileMenuRef)) setShowProfileDropdown(false);
            if (isOutside(event, settingsDropdownRef, settingsMenuRef)) setShowSettingsDropdown(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getInitials = (name: string): string => {
        if (!name || name === '\u00A0' || name === 'Användarnamn saknas') {
            return '';
        }
        return name
            .split(' ')
            .map(word => word.charAt(0).toUpperCase())
            .join('')
            .substring(0, 3); // Limit to 3 initials
    };

    return (
        <div className="navigation-container">
            <MobileMenu
                mainNavItems={mainNavItems}
                isAdmin={isAdmin}
                isSuperadmin={isSuperadmin}
                userName={userName}
                organizationName={organizationName}
                customerLogo={customerLogo}
                activeTodosCount={activeTodosCount}
                logOut={logOut}
                onOpenAssistant={showAssistantEntry ? () => setShowAssistant(true) : undefined}
            />

            <div className="sidebar">
                <Link
                    to="/"
                    className="menu-logo"
                    title="Till startsidan"
                >
                    <img
                        src="/Images/Menu/mf-logo.png"
                        alt="Molnfastigheter"
                        className="menu-logo-image"
                    />
                </Link>

                <div className="menu-main-items">
                    {mainNavItems.map((item) => item.children ? (
                        <MainNavFlyout key={item.path} item={item} />
                    ) : (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`menu-icon-button ${item.isActive ? 'active' : ''}`}
                            title={item.label}
                            aria-current={item.isActive ? 'page' : undefined}
                        >
                            <img src={item.icon} alt="" className="menu-icon-image" />
                            <span className="menu-item-label">{item.label}</span>
                        </Link>
                    ))}
                </div>

                <div className="menu-bottom">
                    {showAssistantEntry && (
                        <button
                            type="button"
                            className={`menu-icon-button assistant-menu-button ${showAssistant ? 'active' : ''}`}
                            onClick={() => setShowAssistant(!showAssistant)}
                            aria-pressed={showAssistant}
                        >
                            <Sparkles className="menu-icon-image menu-icon-lucide" color="#fff" strokeWidth={1.75} aria-hidden="true" />
                            <span className="menu-item-label">Assistent</span>
                        </button>
                    )}
                    {isAdmin && (
                        <div className="settings-dropdown-container" ref={settingsDropdownRef}>
                            <button
                                type="button"
                                className={`menu-icon-button settings-button ${location.pathname.startsWith('/settings') ? 'active' : ''}`}
                                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                            >
                                <img
                                    src="/Images/Menu/meny-settings.png"
                                    alt="Settings"
                                    className="menu-icon-image"
                                />
                                <span className="menu-item-label">Inställningar</span>
                            </button>

                            {showSettingsDropdown && (
                                <SidebarPortal>
                                    <div className="settings-dropdown" ref={settingsMenuRef}>
                                        <div className="settings-dropdown-header">
                                            <span className="settings-dropdown-title">Inställningar</span>
                                        </div>
                                        <div className="settings-dropdown-divider" />
                                        {settingsMenuItems
                                            .filter((item) => (!item.superadminOnly || isSuperadmin) && isFeatureVisible(item.feature, features))
                                            .map((item) => (
                                                <React.Fragment key={item.path}>
                                                    {item.dividerBefore && <div className="settings-dropdown-divider" />}
                                                    <Link
                                                        to={item.path}
                                                        className="settings-dropdown-item"
                                                        onClick={() => setShowSettingsDropdown(false)}
                                                    >
                                                        <item.icon size={18} />
                                                        <span>{item.label}</span>
                                                    </Link>
                                                </React.Fragment>
                                            ))}
                                    </div>
                                </SidebarPortal>
                            )}
                        </div>
                    )}

                    <div className="profile-dropdown-container" ref={profileDropdownRef}>
                        <button
                            type="button"
                            className={`menu-icon-button profile-button ${['/profile', '/change-password', '/my-todos', '/my-notes'].includes(location.pathname) ? 'active' : ''}`}
                            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                            title={`${userName} – ${organizationName}`}
                        >
                            {customerLogo ? (
                                <img
                                    src={customerLogo}
                                    alt="Kundlogotyp"
                                    className="profile-logo-image"
                                />
                            ) : (
                                <img
                                    src="/Images/Menu/meny-user.png"
                                    alt="Profil"
                                    className="profile-logo-image"
                                />
                            )}
                            <div className="profile-label-container">
                                <span className="menu-item-label">{getInitials(userName)}</span>
                                <ShrinkToFitLabel className="menu-item-label profile-org-label" text={organizationName} />
                            </div>
                        </button>

                        {showProfileDropdown && (
                            <SidebarPortal>
                                <div className="profile-dropdown" ref={profileMenuRef}>
                                    <div className="profile-dropdown-header">
                                        <span className="profile-dropdown-name">{userName}</span>
                                        <span className="profile-dropdown-org">{organizationName}</span>
                                    </div>
                                    <div className="profile-dropdown-divider" />
                                    {profileMenuItems.map((item) => (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className="profile-dropdown-item"
                                            onClick={() => setShowProfileDropdown(false)}
                                        >
                                            <item.icon size={18} />
                                            <span>{item.label}</span>
                                            {item.path === '/my-todos' && activeTodosCount > 0 && (
                                                <span className="profile-dropdown-badge">{activeTodosCount}</span>
                                            )}
                                        </Link>
                                    ))}
                                    <div className="profile-dropdown-divider" />
                                    <button
                                        type="button"
                                        className="profile-dropdown-item profile-dropdown-logout"
                                        onClick={() => {
                                            setShowProfileDropdown(false);
                                            logOut();
                                        }}
                                    >
                                        <LogOut size={18} />
                                        <span>Logga ut</span>
                                    </button>
                                </div>
                            </SidebarPortal>
                        )}
                    </div>

                    <div className="version-info version-info-compact">
                        <Link to="/versionshistorik" className="version-text" title="Versionshistorik">
                            v{getAppVersion().version}
                        </Link>
                    </div>
                </div>

            </div>

            <div className="main-content">
                <SetupWarningBanner />
                {showAssistantEntry && <AssistantPanel show={showAssistant} onHide={() => setShowAssistant(false)} />}
                <ErrorBoundary>
                    <Outlet />
                </ErrorBoundary>
            </div>
        </div>
    );
};

export default Navigation;
